import React from 'react';
import { Box, Chip } from '@mui/material';
import { resolveDisplay } from 'shared/field-display';
import { getStageColor } from '../../pages/task-manager/task-manager.constants';

/**
 * One tinted chip in a data field's display color (CIN-7177). Same look as the drawer's task status
 * pills (task-status.js): a STAGE_COLORS pair, weight 600, fully rounded. These tinted chips aren't
 * in the Cinch MUI Library, which only has filled and outlined; Justin picked them on 2026-10-02 so
 * field chips match the stage chips people already know.
 */
// forwardRef so a Tooltip can wrap it (the pinned header chips show the field name on hover).
export const FieldChip = React.forwardRef(function FieldChip({
  text,
  color,
  ...props
}, ref) {
  const {
    bg,
    text: textColor
  } = getStageColor(color);
  return <Chip ref={ref} size="small" label={text} sx={{
    backgroundColor: bg,
    color: textColor,
    fontWeight: 600,
    borderRadius: '100px',
    maxWidth: '100%'
  }} {...props} />;
});

/**
 * A chip-display field's value (check isChipDisplay first; plain fields keep their caller's
 * formatting). `empty` shows when there's nothing to show: no value, or a rule that hides it. A
 * value no rule matches, with no "Any other value" color set, shows as plain text beside the chips.
 */
export function FieldValue({
  field,
  value,
  empty = null
}) {
  const chips = resolveDisplay(field, value);
  if (!chips) return null;
  if (!chips.length) return empty;
  return <Box sx={{
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px'
  }} data-testid="field-value">
      {chips.map((chip, index) => chip.color ? <FieldChip key={index} text={chip.text} color={chip.color} data-testid="field-chip" /> : <span key={index}>{chip.text}</span>)}
    </Box>;
}